import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import '@/components/ui/styles/customScrollBar.css';
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertCircle, CheckCircle2, Download, Eye } from 'lucide-react';
import React from 'react';

const COVDetailsRenderer = ({ data, handlePreview, handleDownload, documentsOnRight }) => {
   // Define required documents
   const requiredDocuments = {
      letterOfIntent: "Letter of Intent",
      tallySheet: "Tally Sheet",
      forestCertification: "Forest Certification",
      orCr: "OR/CR",
      driverLicense: "Driver's License"
   };

   // Optional documents
   const optionalDocuments = {
      specialPowerOfAttorney: "Special Power of Attorney"
   };

   // Check for missing required documents
   const missingDocuments = Object.entries(requiredDocuments).filter(([key]) => {
      return !data.files?.[key] || data.files[key].length === 0;
   });

   // Original renderFileSection function
   const renderFileSection = (files, title) => {
      if (!files || !Array.isArray(files) || files.length === 0) return null;

      return (
         <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">{title}</h4>
            <div className="grid grid-cols-1 gap-2">
               {files.map((file, index) => (
                  <div
                     key={index}
                     className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
                  >
                     <span className="text-sm text-gray-600 dark:text-gray-300">{file.filename}</span>
                     <div className="flex gap-2">
                        <TooltipProvider delayDuration={250}>
                           <Tooltip>
                              <TooltipTrigger asChild>
                                 <Button
                                    size="icon"
                                    variant="outline"
                                    className="dark:border-gray-600 dark:hover:bg-gray-700"
                                    onClick={() => handlePreview(file)}
                                 >
                                    <Eye className="h-4 w-4" />
                                 </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                 <p>Preview</p>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={250}>
                           <Tooltip>
                              <TooltipTrigger asChild>
                                 <Button
                                    size="icon"
                                    variant="outline"
                                    className="dark:border-gray-600 dark:hover:bg-gray-700"
                                    onClick={() => handleDownload(file)}
                                 >
                                    <Download className="h-4 w-4" />
                                 </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                 <p>Download</p>
                              </TooltipContent>
                           </Tooltip>
                        </TooltipProvider>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      );
   };

   const processFiles = (files) => {
      if (!files) return [];
      return Array.isArray(files) ? files : [files];
   };

   return (
      <>
         {missingDocuments.length > 0 ? (
            <Alert variant="destructive" className="mb-4 dark:bg-red-900/20 dark:border-red-900 dark:text-red-200">
               <AlertCircle className="h-4 w-4 dark:text-red-200" />
               <AlertTitle className="dark:text-red-200">Missing Required Documents</AlertTitle>
               <AlertDescription className="dark:text-red-200/90">
                  The following required documents are missing:
                  <ul className="list-disc list-inside mt-2">
                     {missingDocuments.map(([key, label]) => (
                        <li key={key}>{label}</li>
                     ))}
                  </ul>
               </AlertDescription>
            </Alert>
         ) : (
            <Alert variant="success" className="mb-4 bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-900">
               <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
               <AlertTitle className="text-green-600 dark:text-green-400">Documentation Complete</AlertTitle>
               <AlertDescription className="text-green-600 dark:text-green-400/90">
                  All required documents have been submitted.
               </AlertDescription>
            </Alert>
         )}

         <div className="grid grid-cols-2 gap-4">
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Name</h3>
               <p className="dark:text-gray-200">{data.name || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Address</h3>
               <p className="dark:text-gray-200">{data.address || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Cellphone</h3>
               <p className="dark:text-gray-200">{data.cellphone || 'N/A'}</p>
            </div>
            <div>
               <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Purpose</h3>
               <p className="whitespace-pre-wrap dark:text-gray-200">{data.purpose || 'N/A'}</p>
            </div>
         </div>

         <Separator className="my-4 dark:bg-gray-700" />

         <div className="space-y-4">
            <h3 className="font-semibold dark:text-gray-200">Transportation Details</h3>
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Driver Name</h3>
                  <p className="dark:text-gray-200">{data.driverName || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Driver Address</h3>
                  <p className="dark:text-gray-200">{data.driverAddress || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Driver License Number</h3>
                  <p className="dark:text-gray-200">{data.driverLicenseNumber || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Vehicle Plate Number</h3>
                  <p className="dark:text-gray-200">{data.vehiclePlateNumber || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Origin Address</h3>
                  <p className="dark:text-gray-200">{data.originAddress || 'N/A'}</p>
               </div>
               <div>
                  <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400">Destination Address</h3>
                  <p className="dark:text-gray-200">{data.destinationAddress || 'N/A'}</p>
               </div>
            </div>
         </div>

         {!documentsOnRight && (
            <>
               <Separator className="my-4 dark:bg-gray-700" />
               <div className="space-y-4">
                  <h3 className="font-semibold dark:text-gray-200">Supporting Documents</h3>
                  {missingDocuments.length > 0 ? (
                     <Alert variant="destructive" className="mb-4 dark:bg-red-900/20 dark:border-red-900 dark:text-red-200">
                        <AlertCircle className="h-4 w-4 dark:text-red-200" />
                        <AlertTitle className="dark:text-red-200">Missing Required Documents</AlertTitle>
                        <AlertDescription className="dark:text-red-200/90">
                           The following required documents are missing:
                           <ul className="list-disc list-inside mt-2">
                              {missingDocuments.map(([key, label]) => (
                                 <li key={key}>{label}</li>
                              ))}
                           </ul>
                        </AlertDescription>
                     </Alert>
                  ) : (
                     <Alert variant="success" className="mb-4 bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-900">
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <AlertTitle className="text-green-600 dark:text-green-400">Documentation Complete</AlertTitle>
                        <AlertDescription className="text-green-600 dark:text-green-400/90">
                           All required documents have been submitted.
                        </AlertDescription>
                     </Alert>
                  )}
                  {data.files && (
                     <ScrollArea className="h-[300px] custom-scrollbar">
                        <div className="space-y-4 pr-4">
                           {renderFileSection(processFiles(data.files.letterOfIntent), "Letter of Intent")}
                           {renderFileSection(processFiles(data.files.tallySheet), "Tally Sheet")}
                           {renderFileSection(processFiles(data.files.forestCertification), "Forest Certification")}
                           {renderFileSection(processFiles(data.files.orCr), "OR/CR")}
                           {renderFileSection(processFiles(data.files.driverLicense), "Driver's License")}
                           {renderFileSection(processFiles(data.files.specialPowerOfAttorney), "Special Power of Attorney")}
                        </div>
                     </ScrollArea>
                  )}
               </div>
            </>
         )}
      </>
   );
};

export default COVDetailsRenderer;
